import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import { auth } from '@/lib/api';
import { visibleItems } from './navigation.config';
import useActiveRoute from './useActiveRoute';
import haptics from '@/lib/haptics';

/**
 * MobileBottomNav — bottom-pinned 5-tab nav for <640px viewports.
 *
 * Tabs: Dashboard · Portfolio · Analytics · Reports · MORE
 *
 * Tap targets >= 44px. Active state: amber color + amber top border.
 * MORE button calls openMore() — which opens the MoreDrawer (rendered
 * once at AppShell scope, always available regardless of route).
 *
 * Preview-environment safety: the Emergent platform injects a
 * "Made with Emergent" badge (#emergent-badge) at the bottom-right
 * corner with a very high z-index. This badge does NOT exist in
 * production deployments / installed PWAs. To keep all 5 tabs
 * tappable while in preview, we measure the badge's height at mount
 * and lift the nav by that amount.
 */
export default function MobileBottomNav({ onOpenMore }) {
  const isActive = useActiveRoute();
  const user = auth.currentUser();
  const role = user?.role || 'operator';
  const items = visibleItems(role, 'mobile-bottom');
  const [bottomOffset, setBottomOffset] = useState(0);

  useEffect(() => {
    const measure = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) {
        const r = badge.getBoundingClientRect();
        setBottomOffset(Math.ceil(r.height) + 6);
      } else {
        setBottomOffset(0);
      }
    };
    measure();
    // Badge may mount after us — observe & re-measure.
    const obs = new MutationObserver(measure);
    obs.observe(document.body, { childList: true, subtree: false });
    window.addEventListener('resize', measure);
    return () => {
      obs.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <>
      <div style={{ height: 64 + bottomOffset }} aria-hidden="true" />
      <nav
        data-testid="mobile-tab-bar"
        aria-label="Primary mobile"
        style={{
          position: 'fixed', left: 0, right: 0, bottom: bottomOffset,
          height: 56, zIndex: 9500,
          background: '#0a0a0a',
          borderTop: '1px solid var(--border)',
          display: 'grid',
          gridTemplateColumns: `repeat(${items.length + 1}, 1fr)`,
          paddingBottom: 'env(safe-area-inset-bottom, 0)',
        }}>
        {items.map(({ id, to, label, Icon, mobileTestId, testId }) => {
          const on = isActive(to);
          return (
            <Link
              key={id}
              to={to}
              data-testid={mobileTestId || testId}
              data-active={on}
              aria-current={on ? 'page' : undefined}
              onClick={() => { if (!on) haptics.tick(); }}
              style={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                gap: 2, padding: '4px 2px',
                color: on ? 'var(--accent-amber)' : 'var(--text-secondary)',
                textDecoration: 'none',
                borderTop: on
                  ? '2px solid var(--accent-amber)'
                  : '2px solid transparent',
                minHeight: 44,
              }}>
              <Icon size={18} />
              <span style={{ fontSize: 9, fontWeight: 700,
                             letterSpacing: '0.06em' }}>
                {label.length > 6 ? label.slice(0, 6) : label}
              </span>
            </Link>
          );
        })}
        <button
          data-testid="mobile-tab-more"
          onClick={() => { haptics.tick(); onOpenMore(); }}
          aria-label="Open more menu"
          aria-haspopup="dialog"
          style={{
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: 2, padding: '4px 2px',
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--text-secondary)',
            borderTop: '2px solid transparent',
            minHeight: 44,
          }}>
          <MoreHorizontal size={18} />
          <span style={{ fontSize: 9, fontWeight: 700,
                         letterSpacing: '0.06em' }}>MORE</span>
        </button>
      </nav>
    </>
  );
}
